import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { useHistory } from '@useHistory/useHistoryCompat';
import { DESTINATION_TYPES } from '../../../../containers/destination/constants/destinationTypes';
import { WizardStepBaseProps } from '../../../../wizard/interfaces/wizardStep';
import { useWizardStepData } from '../../../../wizard/hooks/useWizardStepData';
import { SetupGuideDocs } from '../../../../docs/components/setup-guide-docs/SetupGuideDocs';
import useUserService from '../../../../auth/hooks/useUserService';
import useService from '../../../../injector/hooks/useService';
import { isPromiseCancelError } from '../../../../core/utils/promise';
import { REQUIRED_SERVICE_ACC_KEY_FIELDS } from '../../../connector-form/components/google-auth/constants';
import { checkAllRequiredFieldsPresent } from '../../../connector-form/components/google-auth/utils';
import { getErrorMessageFromObj } from '../../../../core/utils/request';
import { AuthorisedAccount, ServiceAccount } from '../../../connector-form/interfaces/auth';
import {
  AuthType,
  ConnectorAuthTypeEnum,
  getAuthTypeDetails
} from '../../../connector-form/interfaces/authType';
import { ReactRouterService } from '../../../../core/services/react-router.service';
import styles from './ServiceAccountAuthSelect.module.scss';
import AuthAPI from '../../../../auth/api/AuthAPI';
import { authorizedAccountsFactory } from '../../../connector-form/utils/authorisedAccount';
import {
  TRACKER_ADD_AUTH_ACCOUNT_CLICK,
  TRACKER_AUTH_ACCOUNT_CLICK,
  TRACKER_AUTH_ACCOUNT_CONTINUE,
  TRACKER_AUTH_ACCOUNT_CONTINUE_CLICK,
  TRACKER_NEW_AUTH_ACCOUNT,
  TRACKER_NEW_SERVICE_ACCOUNT_FAILED,
  TRACKER_NEW_SERVICE_ACCOUNT_SUCCESS,
  TRACKER_SELECT_AUTH_TYPE
} from '../../../connector-form/tracking/connectorFormTracking';
import useSetupGuideCommService from '../../../../docs/hooks/useSetupGuideCommService';
import useAnalyticsTracker from '../../../../analytics/hooks/useAnalyticsTracker';
import {
  AccountEntityTypes,
  AuthAccountProvider
} from '../../../auth-select/interfaces';
import { TARGET_TYPES, TargetTypesEnum } from '../../../../containers/activate-target/models/target-type';
import { DestinationMode } from '../../../../core/interfaces/destination-mode';
import { getDestinationModeKeywords } from '../../../connector-form/utils/utils';
import {
  HdButton,
  HdDocLink,
  HdFileInput,
  HdPane,
  HdRadio,
  HdRadioGroup,
  HdTooltip
} from '../../../../components';
import { SOURCE_TYPES } from '../../../../containers/pipeline/interfaces/source-type';
import { AuthAccountsBox } from '../../../auth-select/components/auth-account-box/AuthAccountBox';
import { NewAuthTrigger } from '../../../auth-select/components/new-auth-trigger/NewAuthTrigger';
import {
  AUTH_ACC_CREATED_AT_QUERY_FLAG,
  AUTH_ACC_CREATED_QUERY_FLAG
} from '../../../connector-form/constants/constants';
import { AuthAccountConsentUrl } from '../../../auth-select/components/auth-environment-selection-dialog/interface';
import useAbortController from '../../../../core/hooks/useAbortController';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import { fetchAuthorisedAccounts } from '../../utils/apiUtil';

interface IAuthSelectProps {
  data: any;
  stepControllerData: {
    provider: AuthAccountProvider;
    targetType: TargetTypesEnum;
    destinationMode: DestinationMode;
  };
  params: any;
  markDirty: Function;
  updateData: (data) => void;
  continueNext: (formData: any) => void;
}

export const dataIdGenerator = getDataIdGenerator('auth-select-type');

function SelectGoogleAuthShimmer() {
  return (
    <>
      <div
        className='center-flex-row cursor-hand px-5 justify-between border-bottom mt-7'
        style={{ height: '64px' }}
        data-id={dataIdGenerator('loading-shimmer')}
      >
        <div className='shimmer shimmer-line' style={{ width: '120px', height: '12px' }} />
        <div className='shimmer shimmer-line' style={{ width: '50px', height: '12px' }} />
      </div>

      <div className='center-flex-row cursor-hand px-5 justify-between' style={{ height: '64px' }}>
        <div className='shimmer shimmer-line' style={{ width: '150px', height: '12px' }} />
        <div className='shimmer shimmer-line' style={{ width: '50px', height: '12px' }} />
      </div>
    </>
  );
}

export function SelectGoogleAuth({
  initialData,
  controller,
  markDirty,
  onDataChange,
  completeStep
}: WizardStepBaseProps) {
  const [data, setData] = useWizardStepData(initialData, onDataChange);

  const updateData = d => {
    setData(s => ({
      ...s,
      ...d
    }));
  };

  const continueNext = d => {
    updateData(d);
    completeStep();
  };

  return (
    <SelectGoogleAuthInner
      continueNext={continueNext}
      data={data}
      markDirty={markDirty}
      updateData={updateData}
      stepControllerData={controller.data}
    />
  );
}

export function SelectGoogleAuthInner({
  continueNext,
  data,
  markDirty,
  updateData,
  stepControllerData
}: Omit<IAuthSelectProps, 'params'>) {
  const { provider, destinationMode = DestinationMode.DESTINATION } = stepControllerData;

  const destinationKeyword = getDestinationModeKeywords(destinationMode).singular;
  const { eventTrack } = useAnalyticsTracker();
  const routerService = useService(ReactRouterService);
  const history = useHistory();
  const { user } = useUserService();

  const isUserAccountDisabled = SOURCE_TYPES[provider.connectorType].isUserAccountDisabled && user.teamId !== 137955;

  const authTypes = Object.values(getAuthTypeDetails(provider));

  const [selectedAuthType, setSelectedAuthType] = useState<AuthType>(isUserAccountDisabled ? authTypes[1] : (data.authType || authTypes[0]));
  const [authorisedAccountId, setAuthorisedAccountId] = useState<number>(data.account?.id);

  const [authorisedAccounts, setAuthorisedAccounts] = useState<
    (ServiceAccount | AuthorisedAccount)[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [showServiceAccountForm, setServiceAcountFormStatus] = useState(false);
  const [consentUrls, setConsentUrls] = useState<AuthAccountConsentUrl[]>();

  const [isServiceAccountKeyFileInvalid, setIsServiceAccountKeyFileInvalid] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>();
  const [submitting, setSubmitting] = useState(false);
  const [serviceAccountEmail, setServiceAccountEmail] = useState();

  const { scrollSBSToSection } = useSetupGuideCommService();

  const { abort, abortControllerRef: abortRef } = useAbortController();
  const [touched, setTouched] = useState(false);

  const isServiceAccountFlow = selectedAuthType.value === ConnectorAuthTypeEnum.SERVICE;
  const isAuthAccountFlow = selectedAuthType.value === ConnectorAuthTypeEnum.OAUTH;

  useEffect(() => {
    fetchAuthAccounts(selectedAuthType);
  }, []);

  const setShowServiceAccountForm = value => {
    setTouched(false);
    setServiceAcountFormStatus(value);
  };

  const scrollToSBSSection = docLink => {
    const trackingProps: any = {
      entityType: provider.entityType
    };

    if (provider.entityType === AccountEntityTypes.SOURCE) {
      trackingProps.sourceType = provider.connectorType;
    } else if (provider.entityType === AccountEntityTypes.TARGET) {
      trackingProps.targetType = provider.connectorType;
    } else {
      trackingProps.destinationType = provider.type;
    }

    scrollSBSToSection(docLink, {
      medium: 'program',
      sourceSection: 'select-auth-type',
      label: 'select-type',
      properties: {
        ...trackingProps
      }
    });
  };

  const entityTypeName = useMemo(() => {
    if (provider.entityType === AccountEntityTypes.SOURCE) {
      return 'Source';
    }

    if (provider.entityType === AccountEntityTypes.TARGET) {
      return 'Target';
    }

    return destinationKeyword;
  }, [destinationKeyword, provider.entityType]);

  const documentationLink = useMemo(() => {
    if (!provider) {
      return null;
    }

    let nodeType = provider.connectorType;
    let nodeTypes: any = SOURCE_TYPES;

    if (provider.entityType === AccountEntityTypes.TARGET) {
      nodeTypes = TARGET_TYPES;
    }

    if (provider.entityType === AccountEntityTypes.DESTINATION) {
      nodeType = provider.type;
      nodeTypes = DESTINATION_TYPES;

      if (destinationMode === DestinationMode.WAREHOUSE && nodeTypes[nodeType]?.warehouseDocLink) {
        return nodeTypes[nodeType].warehouseDocLink;
      }
    }

    return nodeTypes[nodeType]?.docLink ? nodeTypes[nodeType].docLink : null;
  }, [destinationMode, provider]);

  const defaultAnalyticsData = useMemo(
    () => ({
      authType: selectedAuthType,
      entityType: provider.entityType
    }),
    [provider.entityType, selectedAuthType]
  );

  const handleSetAuthAccount = accountId => {
    setShowServiceAccountForm(false);

    eventTrack({
      action: TRACKER_AUTH_ACCOUNT_CLICK,
      properties: {
        authType: selectedAuthType,
        entityType: provider.entityType
      }
    });

    setAuthorisedAccountId(accountId);
  };

  const fetchAuthAccounts = (authType: AuthType) => {
    abort(true);

    setLoading(true);
    fetchAuthorisedAccounts(authType.value, provider, routerService, abortRef.current)
      .then(res => {
        const authAccounts = authorizedAccountsFactory(res.tokens, authType.value);
        setAuthorisedAccounts(authAccounts);

        if (authType.value === ConnectorAuthTypeEnum.OAUTH) {
          setConsentUrls(res?.urls);
        } else {
          setConsentUrls([]);
        }

        if (authType.value === ConnectorAuthTypeEnum.SERVICE && authAccounts.length === 0) {
          setShowServiceAccountForm(true);
        }

        if (authAccounts.length) {
          /**
           * Redirect to next step whenever user adds a new auth account,
           * Since this page has multiple entry points, we can use query params
           * as hint to know if the intention is to redirect.
           */
          const searchParams = new URLSearchParams(history.location.search);
          const authAccCreated = searchParams.get(AUTH_ACC_CREATED_QUERY_FLAG);

          if (authAccCreated) {
            let addedAuthorisedAccount = authAccounts[0];

            const authAccCreatedAt = searchParams.get(AUTH_ACC_CREATED_AT_QUERY_FLAG);
            searchParams.delete(AUTH_ACC_CREATED_QUERY_FLAG);
            searchParams.delete(AUTH_ACC_CREATED_AT_QUERY_FLAG);

            if (authAccCreatedAt) {
              addedAuthorisedAccount =
                authAccounts.find(
                  account => account.createdAt.valueOf().toString() === authAccCreatedAt
                ) || addedAuthorisedAccount;
            }

            history.replace({
              search: searchParams.toString()
            });

            /**
             * continue results in another navigation which can cancel the previous navigation
             */
            setTimeout(() => {
              eventTrack({
                action: TRACKER_NEW_AUTH_ACCOUNT,
                properties: {
                  entityType: provider.entityType
                }
              });

              continueNext({
                account: addedAuthorisedAccount,
                authType
              });
            }, 0);
          } else {
            const isSelectedAccountIdValid =
              authorisedAccountId &&
              authAccounts.find(account => account.id === authorisedAccountId);

            if (isSelectedAccountIdValid) {
              setLoading(false);
              return;
            }

            setAuthorisedAccountId(authAccounts[0].id);
            updateData({
              account: authAccounts[0],
              authType
            });
          }
        }

        setLoading(false);
      })
      .catch(err => {
        if (isPromiseCancelError(err)) {
          return;
        }
        setLoading(false);
      });
  };

  const onAuthTypeSelection = (authType: AuthType, trackAuthTypeSelection = true) => {
    // this guard won't work as radio will prevent onChange from triggering
    // if selected option is clicked again
    if (selectedAuthType.value === authType.value) {
      return;
    }

    if (!data.authType || data.authType.value !== authType.value) {
      markDirty();
    }

    updateData({
      authType
    });
    setSelectedAuthType(authType);
    setShowServiceAccountForm(false);

    scrollToSBSSection(authType.docLink);

    setAuthorisedAccounts([]);
    setAuthorisedAccountId(null);

    if (trackAuthTypeSelection) {
      eventTrack({
        action: TRACKER_SELECT_AUTH_TYPE,
        properties: defaultAnalyticsData
      });
    }

    fetchAuthAccounts(authType);
  };

  const onNewAccountClick = () => {
    eventTrack({
      action: TRACKER_ADD_AUTH_ACCOUNT_CLICK,
      properties: defaultAnalyticsData
    });

    if (isServiceAccountFlow) {
      setShowServiceAccountForm(true);
      setAuthorisedAccountId(null);
    } else if (isAuthAccountFlow) {
      if (consentUrls && consentUrls.length === 1) {
        window.location.assign(consentUrls[0].url);
      }
    }
  };

  const deleteAccount = accountId => {
    (() => {
      if (isServiceAccountFlow) {
        return AuthAPI.deleteServiceAccount(accountId);
      }
      if (isAuthAccountFlow) {
        return AuthAPI.deleteAccount(provider.type, accountId);
      }
      return null;
    })().then(() => {
      const filteredAccounts = authorisedAccounts.filter(
        authorisedAccount => authorisedAccount.id !== accountId
      );

      if (authorisedAccountId === accountId) {
        if (filteredAccounts.length > 0) {
          handleSetAuthAccount(filteredAccounts[0].id);
        } else {
          handleSetAuthAccount(null);
        }
      }

      setAuthorisedAccounts(filteredAccounts);
    });
  };

  const continueClick = () => {
    eventTrack({
      action: TRACKER_AUTH_ACCOUNT_CONTINUE_CLICK,
      properties: defaultAnalyticsData
    });

    onContinue();
  };

  const onContinue = (
    authorisedAccountsList = authorisedAccounts,
    selectedAccountId = authorisedAccountId
  ) => {
    if (!data.account || data.account.id !== selectedAccountId) {
      markDirty();
    }

    eventTrack({
      action: TRACKER_AUTH_ACCOUNT_CONTINUE,
      properties: defaultAnalyticsData
    });

    const selectedAccount = authorisedAccountsList.find(
      account => account.id === selectedAccountId
    );

    continueNext({
      account: selectedAccount,
      authType: selectedAuthType
    });
  };

  const onServiceAccountKeyFileChange = file => {
    setIsServiceAccountKeyFileInvalid(false);

    setSelectedFile(file);
    if (!file) {
      return;
    }

    let parsedServiceAccount;
    try {
      parsedServiceAccount = JSON.parse(file?.content || {});
    } catch (e) {
      setIsServiceAccountKeyFileInvalid(true);
      return;
    }

    if (checkAllRequiredFieldsPresent(parsedServiceAccount, REQUIRED_SERVICE_ACC_KEY_FIELDS)) {
      setServiceAccountEmail(parsedServiceAccount.client_email);
      return;
    }

    setIsServiceAccountKeyFileInvalid(true);
  };

  const addServiceAccount = () => {
    setTouched(true);

    if (!selectedFile || !serviceAccountEmail) {
      return;
    }

    setSubmitting(true);

    AuthAPI.addServiceAccount(provider.type, serviceAccountEmail, selectedFile.content).then(
      res => {
        setSubmitting(false);

        if (res.data) {
          const newAccount: ServiceAccount = new ServiceAccount(res.data);

          const authorisedAccountsList = [...authorisedAccounts, newAccount];
          setAuthorisedAccounts(authorisedAccountsList);

          eventTrack({
            action: TRACKER_NEW_SERVICE_ACCOUNT_SUCCESS,
            properties: defaultAnalyticsData
          });

          setAuthorisedAccountId(newAccount.id);

          onContinue(authorisedAccountsList, newAccount.id);
        }
      },
      err => {
        setSubmitting(false);

        eventTrack({
          action: TRACKER_NEW_SERVICE_ACCOUNT_FAILED,
          properties: {
            message: getErrorMessageFromObj(err),
            entityType: provider.entityType
          }
        });
      }
    );
  };

  const isServiceAccountKeyFieldErrored =
    !!isServiceAccountKeyFileInvalid || (touched && !selectedFile);

  return (
    <div className='h-100' data-id={dataIdGenerator('')}>
      <div className='d-flex h-100'>
        <div className='bg-surface flex-1 scrollable-content'>
          <div className='body-container' data-id={dataIdGenerator('container')}>
            <div className='header-container'>
              <div className='header'>Configure your {provider.typeDisplay} account</div>

              <div className='subheader'>
                To know more about configuring this {entityTypeName}, you can read the
                <HdDocLink
                  dataId={dataIdGenerator('')}
                  label='Documentation'
                  className='ml-1'
                  section='settings_header'
                  docLink={documentationLink}
                />
              </div>
            </div>

            {!isUserAccountDisabled ? (
              <div className='center-flex-row justify-center mb-8'>
                <HdRadioGroup name='auth-type-group' currentValue={selectedAuthType.value}>
                  {authTypes.map((authType, idx) => (
                    <HdRadio
                      key={idx}
                      dataId={dataIdGenerator(authType.name)}
                      onChange={() => onAuthTypeSelection(authType)}
                      className={clsx({ 'mr-7': idx !== authTypes.length - 1 })}
                      value={authType.value}
                    >
                      <span className='text-medium pl-1 text-default'>{authType.name}</span>
                    </HdRadio>
                  ))}
                </HdRadioGroup>
              </div>
            ) : null}

            <div className='mx-auto' style={{ maxWidth: '650px' }}>
              {loading && <SelectGoogleAuthShimmer />}

              {authorisedAccounts.length > 0 && (
                <>
                  <div data-id={dataIdGenerator('provider')} className='my-7 text-bold text-center'>
                    Select a {provider.typeDisplay} account
                  </div>

                  <AuthAccountsBox
                    authorisedAccounts={authorisedAccounts}
                    selectedAccountId={authorisedAccountId}
                    provider={provider.type}
                    onAccountChange={(accountId: number) => handleSetAuthAccount(accountId)}
                    entityType={provider.entityType}
                    connectorType={provider.connectorType}
                    isProviderTypeGoogle={provider.isAccountTypeGoogle}
                    onNewAccountClick={() => onNewAccountClick()}
                    authType={selectedAuthType.value}
                    onDeleteAccount={(accountId: number) => deleteAccount(accountId)}
                  />

                  {!showServiceAccountForm && (
                    <div className='center-flex-row justify-center mt-7'>
                      <HdTooltip
                        title='Select an account to proceed'
                        disabled={!!authorisedAccountId}
                        dataId={dataIdGenerator('continue')}
                      >
                        <HdButton
                          disabled={!authorisedAccountId}
                          onClick={() => {
                            continueClick();
                          }}
                          dataId={dataIdGenerator('continue')}
                        >
                          Continue
                        </HdButton>
                      </HdTooltip>
                    </div>
                  )}
                </>
              )}

              {isServiceAccountFlow &&
                (showServiceAccountForm || authorisedAccounts.length === 0) &&
                !loading && (
                  <>
                    <div className='row mt-7'>
                      <div className='col-xs-12'>
                        <HdFileInput
                          dataId={dataIdGenerator('service-account')}
                          showError={isServiceAccountKeyFieldErrored}
                          onChangeEventHandler={onServiceAccountKeyFileChange}
                          label='Service Account Key (JSON) *'
                          accept='application/json'
                          helperText={
                            touched &&
                            !selectedFile && (
                              <span className='mat-error error-transition'>
                                Key file is required
                              </span>
                            )
                          }
                        />
                      </div>
                    </div>

                    {serviceAccountEmail && (
                      <div
                        className='text-center my-3'
                        data-id={dataIdGenerator('service-account-email')}
                      >
                        Your service account email id is
                        <span className='ml-1 text-default text-bold'>{serviceAccountEmail}</span>
                      </div>
                    )}

                    {isServiceAccountKeyFileInvalid && (
                      <HdPane
                        icon='error-filled'
                        className='mt-3'
                        variant='error-faded'
                        dataId={dataIdGenerator('file-upload-fail')}
                      >
                        Please upload a valid JSON file.
                      </HdPane>
                    )}

                    <div className='center-flex-row justify-center mt-5'>
                      <HdButton
                        type='submit'
                        showProgress={submitting}
                        onClick={() => addServiceAccount()}
                        disabled={submitting || isServiceAccountKeyFileInvalid}
                        dataId={dataIdGenerator('submit')}
                      >
                        Configure {provider.typeDisplay} Account
                      </HdButton>
                    </div>
                  </>
                )}

              {isAuthAccountFlow && authorisedAccounts.length === 0 && !loading && (
                <div className='center-flex-row justify-center mt-7'>
                  <NewAuthTrigger
                    triggerLabel={`Add ${provider.typeDisplay} Account`}
                    onAuthClick={() => onNewAccountClick()}
                    flatButton={false}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={clsx(styles.sbsDocs, 'h-100')}>
          <SetupGuideDocs
            docLink='/getting-started/connection-options/google-account-authentication/'
            loadingParent={false}
          />
        </div>
      </div>
    </div>
  );
}
